import React from 'react';
import { Clock, AlertCircle } from 'lucide-react';

/**
 * WhatsApp message preview component that mimics the actual WhatsApp message appearance
 * Used across TemplateManager, CampaignModal, and IncomingMessages
 *
 * @param {Object} props
 * @param {string} props.headerType - Type of header: 'text', 'image', 'video', 'none'
 * @param {string} props.headerText - Text for header when headerType is 'text'
 * @param {string} props.headerMediaUrl - URL for media when headerType is 'image' or 'video'
 * @param {string} props.text - Main message body text
 * @param {Array} props.buttons - Array of button objects with {type, title, id}
 * @param {boolean} props.showContainer - Whether to show the full WhatsApp background container
 * @param {string} props.alignment - Message alignment: 'left' (incoming) or 'right' (outgoing/sent)
 * @param {string} props.profileName - Optional profile name for incoming messages
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.status - Message status: 'pending', 'sent', 'error' (only for outbound messages)
 * @param {string} props.timestamp - Optional timestamp to display
 */
export function WhatsAppMessagePreview({
  headerType = 'none',
  headerText = '',
  headerMediaUrl = '',
  text = '',
  buttons = [],
  showContainer = true,
  alignment = 'right',
  profileName = null,
  className = '',
  status = null,
  timestamp = null
}) {
  const messageBubbleClass = alignment === 'right'
    ? 'bg-[#D9FDD3]' // Outgoing message (green)
    : 'bg-white';    // Incoming message (white)

  const justifyClass = alignment === 'right'
    ? 'justify-end'
    : 'justify-start';

  const MessageBubble = () => (
    <div className={`max-w-[85%] ${messageBubbleClass} rounded-lg shadow-sm overflow-hidden`}>
      {/* Header */}
      {headerType === 'text' && headerText && (
        <div className="bg-white px-4 py-2 border-b border-gray-200">
          <p className="font-semibold text-gray-900 text-sm">
            {headerText}
          </p>
        </div>
      )}

      {headerType === 'image' && headerMediaUrl && (
        <div className="bg-gray-100">
          <img
            src={headerMediaUrl}
            alt="תצוגה מקדימה"
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center h-48 bg-gray-200">
            <span className="text-gray-500 text-sm">🖼️ תמונה</span>
          </div>
        </div>
      )}

      {headerType === 'video' && headerMediaUrl && (
        <div className="bg-gray-100 relative">
          <div className="flex items-center justify-center h-48 bg-gray-800">
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-30 rounded-full flex items-center justify-center mx-auto mb-2">
                <div className="w-0 h-0 border-t-8 border-t-transparent border-r-0 border-b-8 border-b-transparent border-l-12 border-l-white mr-1"></div>
              </div>
              <span className="text-white text-sm">🎥 סרטון</span>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="px-4 py-2">
        {profileName && alignment === 'left' && (
          <p className="text-xs font-semibold text-pink-600 mb-1">
            {profileName}
          </p>
        )}
        <p className="text-gray-900 whitespace-pre-wrap break-words text-sm">
          {text || "טקסט ההודעה יופיע כאן..."}
        </p>
      </div>

      {/* Buttons */}
      {buttons.length > 0 && (
        <div className="border-t border-gray-300 bg-white">
          {buttons.map((button, idx) => (
            <button
              key={button.id || idx}
              type="button"
              className={`w-full px-4 py-2.5 text-[#00A5F4] hover:bg-gray-50 transition-colors text-sm font-medium flex items-center justify-center gap-2 ${
                idx < buttons.length - 1 ? 'border-b border-gray-200' : ''
              }`}
              disabled
            >
              {button.type === 'URL' && '🔗'}
              {button.type === 'PHONE' && '📞'}
              {button.type === 'QUICK_REPLY' && '💬'}
              {button.title || 'כפתור'}
            </button>
          ))}
        </div>
      )}

      {/* Time and Status */}
      <div className="flex items-center justify-end gap-1 px-4 pb-1">
        <span className="text-xs text-gray-600">
          {timestamp || new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
        </span>
        {alignment === 'right' && (
          <>
            {status === 'pending' && (
              <Clock className="w-3 h-3 text-gray-400" />
            )}
            {status === 'error' && (
              <AlertCircle className="w-3 h-3 text-red-500" />
            )}
            {(status === 'sent' || status === 'delivered' || status === 'read' || !status) && (
              <svg className="w-4 h-4 text-gray-600" viewBox="0 0 16 16" fill="none">
                <path d="M13.5 3L6 10.5L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (!showContainer) {
    return (
      <div className={`flex ${justifyClass} mb-2 ${className}`}>
        <MessageBubble />
      </div>
    );
  }

  return (
    <div className={`w-full bg-[#E5DDD5] p-4 ${className}`}>
      {/* Date Separator */}
      <div className="flex justify-center mb-4">
        <div className="bg-white px-3 py-1 rounded-lg shadow-sm">
          <span className="text-xs text-gray-600">היום</span>
        </div>
      </div>

      {/* Message Bubble */}
      <div className={`flex ${justifyClass} mb-2`}>
        <MessageBubble />
      </div>

      {/* Helper text */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-600">
          התצוגה היא קירוב להודעה אמתית
        </p>
      </div>
    </div>
  );
}
